import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAdminSession, AdminLoginForm, LogoutButton, useStripPasswordFromUrl } from "../lib/adminAuth";
import SEOHead from "../components/SEOHead";

export default function AdminStats() {
  useStripPasswordFromUrl();
  const { password, login, logout, isAuthed } = useAdminSession();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loginErr, setLoginErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchData = async (pwd) => {
    setBusy(true);
    setErr(null);
    try {
      const d = await api.adminStats(pwd);
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (isAuthed && password && !data) fetchData(password);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthed]);

  const onLogin = async (pwd) => {
    setBusy(true);
    setLoginErr(null);
    try {
      await login(pwd, api.adminStats);
      const d = await api.adminStats(pwd);
      setData(d);
    } catch (e) {
      setLoginErr(e?.response?.data?.detail || "Invalid password");
    } finally {
      setBusy(false);
    }
  };

  const onLogout = () => {
    logout();
    setData(null);
    setErr(null);
  };

  return (
    <div className="relative z-10 mx-auto min-h-[100dvh] w-full max-w-3xl px-6 py-14" data-testid="admin-stats-screen">
      <SEOHead noindex path="/admin/stats" title="Admin — Stats" />
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-medium uppercase" style={{ color: "var(--muted)", letterSpacing: "0.32em" }}>
            Internal · Analytics
          </p>
          <h1 className="mt-3 font-serif text-[36px]" style={{ color: "var(--gold)" }}>
            Stats
          </h1>
        </div>
        {isAuthed && <LogoutButton onLogout={onLogout} testid="admin-stats-logout" />}
      </div>

      {!isAuthed && <AdminLoginForm onSubmit={onLogin} busy={busy} error={loginErr} testidPrefix="admin-stats" />}

      {err && <p className="mt-6 text-[13px]" style={{ color: "#e07070" }} data-testid="admin-error">{err}</p>}

      {isAuthed && data && (
        <div className="mt-10 flex flex-col gap-8" data-testid="admin-stats-data">
          <StatsTable title="Last 24h" data={data.last_24h} events={data.events} />
          <StatsTable title="Last 7d" data={data.last_7d} events={data.events} />
          <StatsTable title="All time" data={data.all_time} events={data.events} />
          <div className="panel">
            <p className="text-[10px] font-medium uppercase" style={{ color: "var(--gold)", letterSpacing: "0.32em" }}>
              Funnel · All time
            </p>
            <ul className="mt-4 flex flex-col gap-2 font-serif text-[15px]">
              {Object.entries(data.funnel_all_time).map(([k, v]) => (
                <li key={k} className="flex justify-between border-b pb-2" style={{ borderColor: "rgba(154,151,164,0.12)" }}>
                  <span style={{ color: "var(--text)" }}>{k}</span>
                  <span style={{ color: "var(--gold-light)" }}>{v}</span>
                </li>
              ))}
            </ul>
          </div>

          {data.email_delivery !== undefined && (
            <div className="panel" data-testid="email-delivery-panel">
              <p className="text-[10px] font-medium uppercase" style={{ color: "var(--gold)", letterSpacing: "0.32em" }}>
                Email delivery (Resend webhook)
              </p>
              <ul className="mt-4 flex flex-col gap-2 font-serif text-[15px]">
                {Object.keys(data.email_delivery || {}).length === 0 && (
                  <li style={{ color: "var(--muted)" }}>No emails sent yet.</li>
                )}
                {Object.entries(data.email_delivery || {}).map(([k, v]) => (
                  <li key={k} className="flex justify-between border-b pb-2" style={{ borderColor: "rgba(154,151,164,0.12)" }}>
                    <span style={{ color: "var(--text)" }}>{k}</span>
                    <span style={{ color: "var(--gold-light)" }}>{v}</span>
                  </li>
                ))}
                <li className="mt-2 flex justify-between pt-2" style={{ color: "#e07070" }}>
                  <span>Bounced report links (needs manual follow-up)</span>
                  <span data-testid="bounced-reports-count">{data.bounced_report_links || 0}</span>
                </li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatsTable({ title, data, events }) {
  return (
    <div className="panel">
      <p className="text-[10px] font-medium uppercase" style={{ color: "var(--gold)", letterSpacing: "0.32em" }}>
        {title}
      </p>
      <ul className="mt-4 flex flex-col gap-2 font-serif text-[15px]">
        {events.map((e) => (
          <li key={e} className="flex justify-between border-b pb-2" style={{ borderColor: "rgba(154,151,164,0.12)" }}>
            <span style={{ color: "var(--text)" }}>{e}</span>
            <span
              style={{ color: "var(--gold-light)" }}
              data-testid={`stat-${e}-${title.toLowerCase().replace(/ /g, "-")}`}
            >
              {data[e] || 0}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
